import type { ItemState } from '@/contexts/tracker-context';

/**
 * Maps tracker item IDs to OoTR logic item names.
 *
 * For 'progressive' items, the tracker's `state` field (0-indexed after obtaining)
 * maps to OoTR's progressive count: obtained + state 0 = count 1, state 1 = count 2, etc.
 */

interface ItemMapping {
  ootrName: string;
  /** If true, the count = state + 1 (for progressive items like hookshot, scale, strength) */
  progressive?: boolean;
}

const ITEM_MAP: Record<string, ItemMapping> = {
  // Equipment
  Oot_Hookshot:      { ootrName: 'Progressive_Hookshot', progressive: true },
  Oot_Scale:         { ootrName: 'Progressive_Scale', progressive: true },
  Oot_Strength:      { ootrName: 'Progressive_Strength_Upgrade', progressive: true },
  Oot_Wallet:        { ootrName: 'Progressive_Wallet', progressive: true },
  Oot_Bow:           { ootrName: 'Bow', progressive: true },
  Oot_Bomb:          { ootrName: 'Bomb_Bag', progressive: true },
  Oot_Slingshot:     { ootrName: 'Slingshot', progressive: true },
  Oot_Ocarina:       { ootrName: 'Ocarina', progressive: true },
  Oot_Magic:         { ootrName: 'Magic_Meter', progressive: true },

  // Simple items
  Oot_Boomerang:     { ootrName: 'Boomerang' },
  Oot_Hammer:        { ootrName: 'Megaton_Hammer' },
  Oot_IronBoots:     { ootrName: 'Iron_Boots' },
  Oot_HoverBoots:    { ootrName: 'Hover_Boots' },
  Oot_Lens:          { ootrName: 'Lens_of_Truth' },
  Oot_MagicBeans:    { ootrName: 'Magic_Bean' },
  Oot_GerudoCard:    { ootrName: 'Gerudo_Membership_Card' },
  Oot_StoneOfAgony:  { ootrName: 'Stone_of_Agony' },
  Oot_Bombchu:       { ootrName: 'Buy_Bombchu_10' },

  // Tunics
  Oot_GoronTunic:    { ootrName: 'Goron_Tunic' },
  Oot_ZoraTunic:     { ootrName: 'Zora_Tunic' },

  // Shields
  Oot_DekuShield:    { ootrName: 'Deku_Shield' },
  Oot_HylianShield:  { ootrName: 'Hylian_Shield' },
  Oot_MirrorShield:  { ootrName: 'Mirror_Shield' },

  // Swords
  Oot_KokiriSword:   { ootrName: 'Kokiri_Sword' },
  Oot_MasterSword:   { ootrName: 'Master_Sword' },
  Oot_BiggoronSword: { ootrName: 'Biggoron_Sword' },

  // Spells
  Oot_DinsFire:      { ootrName: 'Dins_Fire' },
  Oot_FaroresWind:   { ootrName: 'Farores_Wind' },
  Oot_NayrusLove:    { ootrName: 'Nayrus_Love' },

  // Arrows
  Oot_FireArrow:     { ootrName: 'Fire_Arrows' },
  Oot_IceArrow:      { ootrName: 'Ice_Arrows' },
  Oot_LightArrow:    { ootrName: 'Light_Arrows' },

  // Bottles
  Oot_Bottle1:       { ootrName: 'Bottle' },
  Oot_Bottle2:       { ootrName: 'Bottle' },
  Oot_Bottle3:       { ootrName: 'Bottle' },
  Oot_RutoLetter:    { ootrName: 'Rutos_Letter' },

  // Sticks/Nuts
  Oot_DekuStick:     { ootrName: 'Sticks' },
  Oot_DekuNut:       { ootrName: 'Nuts' },

  // Spiritual stones
  Oot_KokiriEmerald:   { ootrName: 'Kokiri_Emerald' },
  Oot_GoronRuby:       { ootrName: 'Goron_Ruby' },
  Oot_ZoraSapphire:    { ootrName: 'Zora_Sapphire' },

  // Medallions
  Oot_ForestMedallion: { ootrName: 'Forest_Medallion' },
  Oot_FireMedallion:   { ootrName: 'Fire_Medallion' },
  Oot_WaterMedallion:  { ootrName: 'Water_Medallion' },
  Oot_ShadowMedallion: { ootrName: 'Shadow_Medallion' },
  Oot_SpiritMedallion: { ootrName: 'Spirit_Medallion' },
  Oot_LightMedallion:  { ootrName: 'Light_Medallion' },
};

/**
 * Song names used in the pathing panel checkboxes.
 * Maps display name -> OoTR logic name.
 */
export const SONG_MAP: Record<string, string> = {
  'Zeldas Lullaby':    'Zeldas_Lullaby',
  'Eponas Song':       'Eponas_Song',
  'Sarias Song':       'Sarias_Song',
  'Suns Song':         'Suns_Song',
  'Song of Time':      'Song_of_Time',
  'Song of Storms':    'Song_of_Storms',
  'Minuet of Forest':  'Minuet_of_Forest',
  'Bolero of Fire':    'Bolero_of_Fire',
  'Serenade of Water': 'Serenade_of_Water',
  'Nocturne of Shadow':'Nocturne_of_Shadow',
  'Requiem of Spirit': 'Requiem_of_Spirit',
  'Prelude of Light':  'Prelude_of_Light',
};

/**
 * Builds an OoTR item inventory from TrackerContext items + song selections.
 */
export function buildItemInventory(
  items: ItemState[],
  songs: Record<string, boolean>,
): Record<string, number> {
  const inventory: Record<string, number> = {};

  for (const item of items) {
    if (!item.isObtained) continue;
    const mapping = ITEM_MAP[item.id];
    if (!mapping) continue;

    const count = mapping.progressive ? item.state + 1 : 1;
    const name = mapping.ootrName;
    // Accumulate (bottles add up)
    inventory[name] = (inventory[name] ?? 0) + count;
  }

  // Derived: has_bottle
  const bottleCount = (inventory['Bottle'] ?? 0) + (inventory['Rutos_Letter'] ?? 0);
  if (bottleCount > 0) {
    inventory['has_bottle'] = bottleCount;
  }

  // Derived: found_bombchus
  if (inventory['Buy_Bombchu_10']) {
    inventory['found_bombchus'] = 1;
  }

  // Songs
  for (const [, ootrName] of Object.entries(SONG_MAP)) {
    if (songs[ootrName]) {
      inventory[ootrName] = 1;
    }
  }

  return inventory;
}
